/**
 * Pick among ordered candidates while strongly favouring the first one.
 *
 * The squared logarithmic decay gives the first five ranks approximately
 * 50%, 20%, 13%, 9%, and 8% of the probability mass. It is deliberately based
 * on rank rather than pretending that the opening-name corpus contains game
 * popularity counts.
 */
export function rankedChoiceWeight(index: number): number {
  if (!Number.isInteger(index) || index < 0) return 0;
  return 1 / Math.log2(index + 2) ** 2;
}

export function chooseRanked<T>(
  candidates: readonly T[],
  rng: () => number = Math.random,
): T | undefined {
  if (candidates.length === 0) return undefined;
  const weights = candidates.map((_, index) => rankedChoiceWeight(index));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const sample = Math.min(1 - Number.EPSILON, Math.max(0, rng())) * total;
  let cumulative = 0;
  for (let index = 0; index < candidates.length; index++) {
    cumulative += weights[index];
    if (sample < cumulative) return candidates[index];
  }
  return candidates[candidates.length - 1];
}
